/*
 * Copyright (c) 2026 CalAmp Corp.  All Rights Reserved
 */
import {
  DependencyList,
  RefObject,
  useLayoutEffect,
  useState
} from 'react';

/**
 * useIsOverflowing measures whether the ref'd element's single-line content
 * is actually clipped by ellipsis (`scrollWidth > clientWidth`), re-measuring
 * whenever `deps` changes - used to only show a tooltip with the full content
 * when the text has genuinely overflowed, never on content short enough to
 * fit within the available width.
 *
 * ```typescript
 *
 *   import {useIsOverflowing} from '@ion/dls/src/hooks/useIsOverflowing';
 *   ...
 *
 *   const ref            = useRef<HTMLDivElement>(null);
 *   const isOverflowing  = useIsOverflowing(ref, [children]);
 *
 * ```
 *
 */
export const useIsOverflowing = <T extends HTMLElement>(ref: RefObject<T>, deps: DependencyList): boolean => {
  const [isOverflowing, setOverflowing] = useState(false);

  useLayoutEffect(() => {
    const element = ref.current;
    if (element) {
      setOverflowing(element?.scrollWidth > element?.clientWidth);
    }
  }, deps);

  return isOverflowing;
};

export default useIsOverflowing;
